import React, { useState } from 'react';

import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import classes from './DropdownDate.module.css';

/**
 * DropdownDate is a simple date component used as a date selector alternative to a calendar selector.
 * 
 * @param {startDate, numYears, onChange} props 
 * @returns 
 */
const DropdownDate = ({startDate, currentValue, numYears, onChange}) => {
  const [currentDate, setCurrentDate] = useState(startDate)
  const schema = Yup.object({
    month: Yup.number()
      .min(0, 'Invalid month specified')
      .max(11, "Invalid month specified"),
    day: Yup.number()
      .min(1, "Invalid day")
      .max(31, "Invalid day specified")
      .required("Must specify a valid day within the month selected"),
    year: Yup.number()
      .required("Must specify a valid year")
  });
  
  const formik = useFormik({
    initialValues: {
      month: currentValue?.getMonth(),
      day: currentValue?.getDate(),
      year: currentValue?.getFullYear()
    },
    validateOnChange: true,
    validationSchema: schema,
    onSubmit: (values) => {
      // debugger
      console.log(`DropdownDate: onSubmit: formikErrors: ${formik.errors}`, values)
      // props.onSubmit(values)
    }
  })

  /**
   * @param {number} year - The four-digit year (e.g., 2024).
   * @param {number} month - The month index (0 for January, 11 for December).
   * @returns {number} The number of days in the specified month.
   */
  const getDaysInMonth = (year, month) => {
    // Pass 0 as the day to the Date constructor.
    // The Date object will "roll back" to the last day of the *previous* month.
    // Example: new Date(2024, 2, 0) (March 0th) resolves to February 29, 2024.
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Use getDate() to return the day number, which is the total days in the month.
    return lastDayOfMonth.getDate();
  }

  const getMonthNames = (locale = 'en-US', style = 'long') => {
    const monthNames = [];
  
    // We iterate from month index 0 (January) to 11 (December)
    for (let i = 0; i < 12; i++) {
      // 1. Create a Date object for the first day of the current month
      // The year (2024) is arbitrary, only the month index (i) matters.
      const date = new Date(2024, i, 1);
      
      // 2. Use the Internationalization API to format the date
      const name = new Intl.DateTimeFormat(locale, { month: style }).format(date);
      
      // 3. Add the formatted name to the list
      monthNames.push(name);
    }
    
    return monthNames;
  }

// --- Examples ---

// // 1. Full English Names
// const fullMonths = getMonthNames('en-US', 'long');
// console.log('Full English:', fullMonths);
// // Output: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// // 2. Short German Names
// const shortGermanMonths = getMonthNames('de-DE', 'short');
// console.log('Short German:', shortGermanMonths);
// // Output: ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.']

  const renderMonths = () => {
    const shortNames = getMonthNames('en-US', 'short');
    let months = [];
    for (var i = 1; i <= 12; i++) {
      months.push(<option key={`${shortNames[i]}-${i}`} value={i}>{shortNames[i]}</option>)
    }
    return months;
  }
  const renderDays = () => {
    let days = [];
    const daysInMonth = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth())
    for(var i = 1; i <= daysInMonth; i++) {
      days.push(<option key={`day-${i}`} value={i}>{i}</option>)
    }
    return days;
  }

  const renderYears = () => {
    let years = [];
    let firstYear = new Date();
    for (var i = 0; i < numYears; i++) {
      years.push(<option key={`year-${firstYear.getFullYear()+i}`} value={firstYear.getFullYear()+i}>{firstYear.getFullYear()+i}</option>)
    }
    return years;
  }

  const handleMonthChange = (newMonth) => {
    let newDate = new Date(currentDate.getFullYear(), newMonth, currentDate.getDate())
    setCurrentDate(newDate)
    formik.setFieldValue("month", newMonth)
    onChange(newDate)
  }
  const handleDayChange = (newDay) => {
    let newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), newDay)
    setCurrentDate(newDate)
    formik.setFieldValue("day", newDay)
    onChange(newDate)
  }
  const handleYearChange = (newYear) => {
    let newDate = new Date(newYear, currentDate.getMonth(), currentDate.getDate())
    setCurrentDate(newDate)
    formik.setFieldValue("year", newYear)
    onChange(newDate)
  }
  return (
    <Row>
      <Col sm={12} className="d-flex justify-content-center">
        <InputGroup className={classes.DateDropdown}>
          <Form.Control
            id="month" name="month"
            as="select" 
            value={formik.values.month}
            onChange={(month => {handleMonthChange(month.target.value)})}
            isInvalid={formik.errors.month}
          >
            <option value="0">[Select]</option>
            {renderMonths()}
          </Form.Control>
          <Form.Control.Feedback className={classes.ErrorMessage} type='invalid'>
            {formik.errors.month}
          </Form.Control.Feedback>

        </InputGroup>
      {/* </Col>
      <Col sm={4}> */}
        <InputGroup className={classes.DateDropdown}>
          <Form.Control
            id="day" name="day"
            as="select" 
            value={formik.values.day}
            onChange={(day => {handleDayChange(day.target.value)})}
            isInvalid={formik.errors.day}
          >
            <option value="0">[Select]</option>
            {renderDays()}
          </Form.Control>
          <Form.Control.Feedback className={classes.ErrorMessage} type='invalid'>
            {formik.errors.day}
          </Form.Control.Feedback>

        </InputGroup>
      {/* </Col>
      <Col sm={4}> */}
        <InputGroup className={classes.DateDropdown}>
          <Form.Control
            id="year" name="year"
            as="select" 
            value={formik.values.year}
            onChange={(year => {handleYearChange(year.target.value)})}
            isInvalid={formik.errors.year}
          >
            <option value="0">[Select]</option>
            {renderYears(4)}
          </Form.Control>
          <Form.Control.Feedback className={classes.ErrorMessage} type='invalid'>
            {formik.errors.year}
          </Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Row>
  );
}

export default DropdownDate;